import {
  createSkillResolveSingleFlight,
  type SkillResolveSingleFlight,
  type SkillTurnBootstrapResult,
} from "../../shared/skills/runtime/skillTurnBootstrap";
import {
  isExcelSkillsGateSnapshotCurrent,
  type ExcelSkillsGateSnapshot,
} from "../../shared/featureFlags/pickerSkills";
import type { UserSkillRunContext } from "./runContext";

export { createSkillResolveSingleFlight, type SkillResolveSingleFlight };

/**
 * What the Excel run should do once resolution settles.
 *
 * `blocked` is the fail-closed branch: the user asked for specific Skills and
 * we could not prove which guidance applies, so the run must not start rather
 * than silently answering without the Skill.
 */
export type ExcelUserSkillTurnOutcome =
  | { readonly kind: "proceed"; readonly context?: UserSkillRunContext }
  | { readonly kind: "blocked"; readonly message: string }
  | { readonly kind: "aborted" };

const DEFAULT_BLOCKED_MESSAGE =
  "The selected Skills could not be applied, so this request was not sent. Try again in a moment.";

/**
 * Thrown to stop a run whose explicitly selected Skills failed to resolve.
 *
 * Carries no partial progress and offers no continuation: the run never
 * started, so there is nothing to resume.
 */
export class ExcelSkillsBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExcelSkillsBlockedError";
    Object.setPrototypeOf(this, ExcelSkillsBlockedError.prototype);
  }
}

/**
 * Maps a settled bootstrap into the Excel run decision.
 *
 * Explicit selection fails closed; automatic selection fails open, because a
 * Skill the user never chose must not be able to break an ordinary prompt.
 */
export function classifyExcelUserSkillTurn(
  result: SkillTurnBootstrapResult
): ExcelUserSkillTurnOutcome {
  if (result.status === "aborted") return { kind: "aborted" };
  if (result.status === "blocked") {
    return {
      kind: "blocked",
      message: result.failure?.message?.trim() || DEFAULT_BLOCKED_MESSAGE,
    };
  }
  return result.context ? { kind: "proceed", context: result.context } : { kind: "proceed" };
}

/**
 * Awaits the in-flight resolution and re-checks the rollout gate.
 *
 * The gate is re-read after the await so a flag flip mid-resolution cancels
 * the run instead of applying guidance the user is no longer entitled to.
 */
export async function settleExcelUserSkillTurn(
  pending: Promise<SkillTurnBootstrapResult>,
  gateSnapshot: ExcelSkillsGateSnapshot
): Promise<{
  readonly outcome: ExcelUserSkillTurnOutcome;
  readonly result: SkillTurnBootstrapResult;
}> {
  const result = await pending;
  if (!isExcelSkillsGateSnapshotCurrent(gateSnapshot)) {
    return { outcome: { kind: "aborted" }, result };
  }
  return { outcome: classifyExcelUserSkillTurn(result), result };
}

/** Analytics status label; `disabled` is reported as `none` for parity with PowerPoint. */
export function toSkillAnalyticsStatus(
  result: SkillTurnBootstrapResult
): Exclude<SkillTurnBootstrapResult["status"], "disabled"> | "none" {
  return result.status === "disabled" ? "none" : result.status;
}
